import { useCallback, useEffect, useRef, useState } from "react";
import { HardDrive, Loader2, Pencil } from "lucide-react";
import { formatBytes } from "@/lib/formatBytes";
import { fetchMailStorageStats, type MailStorageStats } from "@/lib/api/storage";
import type { AccountSettingsFocus } from "@/features/accounts/accountSettingsFocus";
import { cn } from "@/lib/cn";

const WARN_PERCENT = 80;

type Props = {
  accountId: number;
  onOpenAccountSettings?: (focus?: AccountSettingsFocus) => void;
};

let statsRefreshTimer: number | null = null;

/** Quota serveur (lecture seule). La copie .eml est suivie dans le journal. */
export function MailStorageQuotaSection({ accountId, onOpenAccountSettings }: Props) {
  const [stats, setStats] = useState<MailStorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadStats = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const res = await fetchMailStorageStats(accountId);
      if (mountedRef.current && res.ok && res.data.success) {
        setStats(res.data);
      }
    } finally {
      if (mountedRef.current && !soft) setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void loadStats(false);
  }, [loadStats]);

  useEffect(() => {
    const onRefresh = () => void loadStats(true);
    window.addEventListener("mail-storage-stats-refresh", onRefresh);
    return () => window.removeEventListener("mail-storage-stats-refresh", onRefresh);
  }, [loadStats]);

  const usedBytes = stats?.usedBytes ?? 0;
  const maxBytes = stats?.maxBytes ?? 0;
  const usedPercent = maxBytes > 0 ? Math.min(100, (usedBytes / maxBytes) * 100) : 0;
  const barPercent = Math.min(100, Math.max(0, usedPercent));
  const isWarn = usedPercent >= WARN_PERCENT;

  const messageCount = stats?.emlMessageCount ?? 0;
  const messageBytes = stats?.emlUsedBytes ?? 0;

  const summaryLabel =
    messageCount > 0
      ? `${messageCount} e-mail${messageCount !== 1 ? "s" : ""}${messageBytes > 0 ? ` · ${formatBytes(messageBytes)}` : ""}`
      : "Copies .eml sur le serveur Aurélia";

  return (
    <div className="rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)]/80 px-2.5 py-2">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="inline-flex min-w-0 items-center gap-1 text-[11px] font-medium text-[var(--mail-text)]">
          <HardDrive className="h-3 w-3 shrink-0 text-[var(--mail-muted)]" aria-hidden />
          Stockage serveur
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin text-[var(--mail-muted)]" aria-hidden />
          ) : (
            <span
              className={cn(
                "text-[10px] tabular-nums",
                isWarn ? "font-semibold text-amber-700" : "text-[var(--mail-muted)]"
              )}
            >
              {formatBytes(usedBytes)} / {formatBytes(maxBytes)}
            </span>
          )}
          <button
            type="button"
            onClick={() => onOpenAccountSettings?.("account")}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
            aria-label="Ouvrir les paramètres stockage"
          >
            <Pencil className="h-3 w-3" aria-hidden />
          </button>
        </span>
      </div>

      {!loading && stats ? (
        <>
          <div
            className="h-1 overflow-hidden rounded-full bg-[var(--mail-surface-hover)]"
            role="progressbar"
            aria-valuenow={barPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Espace utilisé ${Math.round(barPercent)} %`}
          >
            <div
              className={cn(
                "h-full rounded-full transition-all",
                isWarn ? "bg-amber-500" : "bg-[var(--mail-accent)]"
              )}
              style={{ width: `${barPercent}%` }}
            />
          </div>
          <p className="mt-1.5 text-[10px] text-[var(--mail-muted)]">{summaryLabel}</p>
        </>
      ) : null}
    </div>
  );
}

export function notifyMailStorageStatsRefresh(): void {
  if (statsRefreshTimer != null) return;
  statsRefreshTimer = window.setTimeout(() => {
    statsRefreshTimer = null;
    window.dispatchEvent(new CustomEvent("mail-storage-stats-refresh"));
  }, 2_500);
}
